import { Ref, ref } from 'vue';
import { useNotify, NotifyType } from '@/composables/useNotify';
import { useI18n } from 'vue-i18n';
import { hubClient, getApiErrorMessage } from '@tfpl/api-client';
import { getConfig } from '@/config';
import { setPrimaryAbsenceApprover } from '@/utils/auth';
import { useAuthStore } from '@/store/auth';

interface UseUsers {
  loading: Ref<boolean>;
  error: Ref<unknown>;
  showError: Ref<boolean>;
  updateUser: (userUuid: string, dto: hubClient.UpdateCollaboratorDto) => Promise<void>;
  getUniqueLabels: () => Promise<string[]>;
  updateConfirmSecurityTFDate: (userUuid: string) => Promise<void>;
}

export const useUsers: () => UseUsers = () => {
  const { notify } = useNotify();
  const { t } = useI18n();
  const loading = ref(false);
  const error = ref<unknown>();
  const showError = ref(false);
  const setError = (err: boolean): void => {
    showError.value = err;
  };

  const updateUser = async (userUuid: string, dto: hubClient.UpdateCollaboratorDto) => {
    const { currentUser } = useAuthStore();

    loading.value = true;
    error.value = undefined;
    try {
      const { data: response } = await hubClient.V3CollaboratorsService.updateOneV3({
        path: {
          uuid: userUuid,
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        body: dto,
        throwOnError: true,
      });

      if (currentUser?.uuid === userUuid && response.data.primaryOfficeAbsenceApprover) {
        setPrimaryAbsenceApprover(response.data.primaryOfficeAbsenceApprover);
      }

      notify(t('users.changeSuccess'), NotifyType.SUCCESS);
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('users.changeError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const getUniqueLabels = async (): Promise<string[]> => {
    loading.value = true;
    error.value = undefined;
    try {
      const { data: response } = await hubClient.V3CollaboratorsService.getUniqueLabelsV3({
        path: {
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        throwOnError: true,
      });
      return response.data.map((label) => label.name);
    } catch (e) {
      error.value = e;
      setError(true);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const updateConfirmSecurityTFDate = async (userUuid: string) => {
    loading.value = true;
    error.value = undefined;
    try {
      await hubClient.V3CollaboratorsService.updateConfirmSecurityTfDateV3({
        path: {
          uuid: userUuid,
          tenant: getConfig().TENANT,
          company: getConfig().TENANT,
        },
        throwOnError: true,
      });
      notify(t('users.changeSuccess'), NotifyType.SUCCESS);
    } catch (e) {
      error.value = e;
      setError(true);
      notify(t('users.changeError'), NotifyType.ERROR);
      console.log('[error]', getApiErrorMessage(e) ?? e);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  return {
    loading,
    error,
    showError,
    updateUser,
    getUniqueLabels,
    updateConfirmSecurityTFDate,
  };
};
